<template>
  <div class="relative" :style="{ zIndex: dynamicZIndex }" @keydown="handleKeyDown">
    <Listbox v-model="internalValue" :disabled="disabled" :multiple="multiselect" :style="{ zIndex: dynamicZIndex }" @open="open = true" @close="open = false">
      <div class="relative">
        <!-- Button -->
        <ListboxButton
          ref="triggerRef"
          @click="open = !open"
          class="w-full flex items-center justify-between px-3 py-[14px] rounded-xl border relative"
          :class="[error ? 'border-red-500' : 'border-[#E8ECEF]', disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white cursor-pointer', !error && !disabled && success ? 'border-green-500' : '']">
          <div class="flex items-center w-full min-w-0" :class="[size === 'sm' ? 'justify-between' : 'justify-start']">
            <slot name="prefix" />
            <!-- <Icon :name="error ? 'ph:warning' : success ? 'ph:check-circle' : 'ph:check-circle'" :class="error ? 'text-red-500' : success ? 'text-green-500' : 'text-gray-400'" class="absolute left-3 top-1/2 -translate-y-1/2" size="20" /> -->
            <slot name="selected" :option="selectedOption" :options="selectedOptions" class="ml-1">
              <template v-if="multiselect">
                <div v-if="selectedOptions.length" class="flex items-center gap-2 min-w-0">
                  <span class="shrink-0 inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-sm font-medium"> {{ selectedOptions.length }} selected </span>
                  <div class="flex flex-wrap gap-1">
                    <span v-for="(option, index) in selectedOptions.slice(0, props.maxDisplayOptions)" :key="index" class="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-sm">
                      <span v-if="option.flag" class="text-sm">{{ option.flag }}</span>
                      <span>{{ getValueByPath(option, labelKey) }}</span>
                    </span>
                    <span v-if="selectedOptions.length > props.maxDisplayOptions" class="px-2 py-1 bg-gray-100 rounded-md text-sm"> +{{ selectedOptions.length - props.maxDisplayOptions }} more </span>
                  </div>
                </div>
                <span v-else class="text-gray-500">{{ placeholder }}</span>
              </template>
              <span v-else class="truncate" :class="[!selectedOption && 'text-gray-500', size === 'sm' ? 'ml-2' : 'ml-5']">
                {{ selectedOption ? getValueByPath(selectedOption, labelKey) : placeholder }}
              </span>
            </slot>
          </div>
          <Icon name="ph:caret-down" class="flex-shrink-0 w-5 h-5 text-gray-500 transition-transform duration-200 ml-2" :class="{ 'rotate-180': open }" />
        </ListboxButton>

        <!-- Options -->
        <Transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0">
          <teleport to="body">
            <ListboxOptions ref="optionsRef" class="absolute w-[var(--select-width)] bg-white border border-[#E8ECEF] rounded-xl shadow-lg max-h-60 overflow-y-auto focus:outline-none" :style="{ zIndex: dynamicZIndex }">
              <!-- Search Input -->
              <div v-if="searchable" class="sticky top-0 p-2 bg-white border-b border-[#E8ECEF]">
                <input
                  ref="searchInput"
                  type="text"
                  v-model="searchQuery"
                  class="w-full px-3 py-2 text-sm border border-[#E8ECEF] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  :placeholder="searchPlaceholder"
                  @keydown.stop />
              </div>
              <div class="py-1">
                <ListboxOption @click="setOpenFromOption" v-for="option in filteredOptions" :key="getValueByPath(option, valueKey)" :value="getValueByPath(option, valueKey)" v-slot="{ active, selected }" as="template" :disabled="option.disabled">
                  <li class="w-full flex items-center px-4 py-2 text-sm cursor-pointer" :class="[active ? 'bg-gray-50 text-dark' : 'text-gray-900', option.disabled ? 'opacity-50 cursor-not-allowed' : '']">
                    <template v-if="multiselect">
                      <div class="flex items-center gap-2 w-full">
                        <div class="flex-shrink-0 w-4 h-4 border rounded" :class="[selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300']">
                          <Icon v-if="selected" name="ph:check" class="w-4 h-4 text-white" />
                        </div>
                        <slot name="option" :option="option" :active="active" :selected="selected">
                          <div v-if="option.icon" class="flex items-center gap-2">
                            <Icon :name="option.icon" class="w-4 h-4" />
                            {{ getValueByPath(option, labelKey) }}
                          </div>
                          <div v-else>
                            {{ getValueByPath(option, labelKey) }}
                          </div>
                        </slot>
                      </div>
                    </template>
                    <template v-else>
                      <slot name="option" :option="option" :active="active" :selected="selected" class="flex items-center gap-2 w-full">
                        {{ getValueByPath(option, labelKey) }}
                      </slot>
                    </template>
                  </li>
                </ListboxOption>
                <!-- No results message -->
                <li v-if="filteredOptions.length === 0" class="px-4 py-2 text-sm text-gray-500">No results found</li>
              </div>
            </ListboxOptions>
          </teleport>
        </Transition>
      </div>
    </Listbox>
  </div>
</template>

<script setup>
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue"
import { onClickOutside } from "@vueuse/core"
import { onUnmounted } from "vue"

const { getDynamicZIndexWithStacking, releaseDropdownZIndex } = useZIndex()

function getValueByPath(obj, path) {
  return path.split(".").reduce((acc, part) => acc?.[part], obj)
}

const props = defineProps({
  modelValue: {
    type: [String, Number, Object, Array],
    default: null,
  },
  options: {
    type: Array,
    required: true,
  },
  placeholder: {
    type: String,
    default: "Select an option",
  },
  searchPlaceholder: {
    type: String,
    default: "Type to search...",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  success: {
    type: Boolean,
    default: false,
  },
  labelKey: {
    type: String,
    default: "label",
  },
  valueKey: {
    type: String,
    default: "value",
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  multiselect: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: "lg",
  },
  maxDisplayOptions: {
    type: Number,
    default: 2,
  },
})

const emit = defineEmits(["update:modelValue", "change"])

// Initialize internal value based on multiselect mode
const internalValue = ref(props.multiselect ? [] : props.modelValue)
const open = ref(false)
const searchQuery = ref("")
const searchInput = ref(null)
const triggerRef = ref(null)
const optionsRef = ref(null)
const popupStyle = ref({})

// Dynamic z-index based on context with stacking support
const dynamicZIndex = ref(60) // Default z-index for body content
const currentZIndex = ref(null) // Track the z-index assigned to this dropdown

// Update z-index when component mounts or context changes
const updateZIndex = () => {
  if (process.client && triggerRef.value) {
    const element = triggerRef.value?.$el ?? triggerRef.value
    // Use stacking z-index to ensure proper layering when multiple dropdowns are open
    dynamicZIndex.value = getDynamicZIndexWithStacking(element, "dropdown", true)
    currentZIndex.value = dynamicZIndex.value
  }
}
const updatePosition = () => {
  if (triggerRef.value && optionsRef.value) {
    const triggerEl = triggerRef.value?.$el ?? triggerRef.value
    if (!triggerEl || !triggerEl.getBoundingClientRect) return

    const rect = triggerEl.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth
    const dropdownHeight = optionsRef.value.$el?.clientHeight || 240 // fallback to max-h-60
    const dropdownWidth = rect.width

    // Calculate position
    let top = rect.bottom + window.scrollY
    let left = rect.left + window.scrollX

    // Check if dropdown would go below viewport
    if (rect.bottom + dropdownHeight > viewportHeight && rect.top > dropdownHeight) {
      // Show above the input
      top = rect.top + window.scrollY - dropdownHeight
    }

    // Check if dropdown would go beyond right edge of viewport
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 10 // 10px margin from edge
    }

    // Ensure dropdown doesn't go beyond left edge
    if (left < 10) {
      left = 10
    }

    popupStyle.value = {
      top: `${top}px`,
      left: `${left}px`,
      width: `${dropdownWidth}px`,
      position: "absolute",
      zIndex: dynamicZIndex.value, // Use the dynamic z-index instead of hardcoded value
    }

    if (optionsRef.value?.$el) {
      Object.assign(optionsRef.value.$el.style, popupStyle.value)
    }
  }
}
// Add event listeners for dynamic positioning
const addEventListeners = () => {
  if (process.client) {
    window.addEventListener("scroll", updatePosition, true)
    window.addEventListener("resize", updatePosition)
  }
}

const removeEventListeners = () => {
  if (process.client) {
    window.removeEventListener("scroll", updatePosition, true)
    window.removeEventListener("resize", updatePosition)
  }
}

watch(open, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      updateZIndex() // Update z-index when dropdown opens (gets new stacking z-index)
      updatePosition() // short delay to ensure <ListboxOptions> is mounted
      addEventListeners() // Add listeners when dropdown opens
    })
  } else {
    // Release z-index when dropdown closes (optional cleanup)
    if (currentZIndex.value !== null) {
      releaseDropdownZIndex(currentZIndex.value)
      currentZIndex.value = null
    }
    removeEventListeners() // Remove listeners when dropdown closes
  }
})

// Clean up event listeners on component unmount
onUnmounted(() => {
  removeEventListeners()
})

const setOpenFromOption = () => {
  if (props.multiselect) {
    open.value = true
  } else {
    open.value = false
  }
}
onClickOutside(optionsRef, (event) => {
  const triggerEl = triggerRef.value?.$el ?? triggerRef.value
  if (triggerEl && !triggerEl.contains(event.target)) {
    open.value = false
  }
})
// Handle keyboard input when dropdown is open
const handleKeyDown = (e) => {
  // Only handle if dropdown is open and not in search input
  if (open.value && e.target.tagName !== "INPUT") {
    // Ignore special keys
    if (e.ctrlKey || e.altKey || e.metaKey || e.key.length > 1) {
      return
    }
    // Start search with pressed key
    searchQuery.value = e.key
    nextTick(() => {
      if (searchInput.value) {
        searchInput.value.focus()
      }
    })
  }
}

// Reset search when dropdown closes
watch(open, (isOpen) => {
  if (!isOpen) {
    searchQuery.value = ""
  } else {
    // Focus search input when dropdown opens
    nextTick(() => {
      if (searchInput.value) {
        searchInput.value.focus()
      }
    })
  }
})

// Filter options based on search query
const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options

  const query = searchQuery.value.toLowerCase()
  return props.options.filter((option) => {
    const label = getValueByPath(option, props.labelKey)?.toString().toLowerCase() || ""
    return label.includes(query)
  })
})

// Update parent when selection changes
watch(internalValue, (val) => {
  emit("update:modelValue", val)
  if (props.multiselect) {
    const selectedItems = props.options.filter((o) => val.includes(getValueByPath(o, props.valueKey)))
    emit("change", selectedItems)
  } else {
    const found = props.options.find((o) => getValueByPath(o, props.valueKey) === val)
    if (found) emit("change", found)
  }
})

// Find the selected option(s)
const selectedOption = computed(() => {
  return props.options.find((option) => getValueByPath(option, props.valueKey) === internalValue.value)
})

const selectedOptions = computed(() => {
  if (!props.multiselect || !Array.isArray(internalValue.value)) return []
  return props.options.filter((option) => internalValue.value.includes(getValueByPath(option, props.valueKey)))
})

// Watch for external modelValue changes
watch(
  () => props.modelValue,
  (newVal) => {
    if (props.multiselect) {
      internalValue.value = Array.isArray(newVal) ? newVal : []
    } else {
      internalValue.value = newVal
    }
  },
  { immediate: true }
)

// Update z-index on mount
onMounted(() => {
  updateZIndex()
})
</script>
